import { useState } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { ReactComponent as Trash } from "bootstrap-icons/icons/trash3.svg";
import { ReactComponent as PlusSquare } from "bootstrap-icons/icons/plus-square.svg";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as ArrowLeftSquare } from "bootstrap-icons/icons/arrow-left-square-fill.svg";
import Spinner from "../spinner/Spinner";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import CustomStyles from "../../components/utils/CustomStyles";
import { useSubCategories, useDeleteSubCategory } from "../../hooks/useAppData";

const SubCategoryList = () => {
  const [filterText, setFilterText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const categoryData = location.state?.categoryId;
  const categoryId = categoryData ? Number(categoryData.category_id || categoryData) : null;
  const categoryName = categoryData?.name || "";

  const { data: subCategoryResponse, isLoading: loading } = useSubCategories(categoryId);
  const subCategory = subCategoryResponse?.data || [];
  const deleteSubCategoryMutation = useDeleteSubCategory();

  const handleDeleteConfirmation = (subCategory_id) => {
    setConfirmationLoading(true);
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      setConfirmationLoading(false);
      if (result.isConfirmed) {
        deleteSubCategoryMutation.mutate(subCategory_id);
      }
    });
  };

  const handleFilter = (e) => {
    setFilterText(e.target.value);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  const columns = [
    {
      name: "Actions",
      width: "300px",
      cell: (row) => (
        <div className="flex justify-start">
          <Link
            to="/admin/products/category/list"
            state={{ id: row.sub_category_id }}
            className="mx-4 my-2 group"
            style={{ marginLeft: "5px" }}
          >
            <Eye className="w-5 h-5 text-dash mr-3" />
            <div className="absolute ml-12 top-full left-2/2 transform -translate-x-1/2 mb-1 hidden group-hover:block text-dash5 text-xs whitespace-nowrap">
              product category
            </div>
          </Link>
          <Link
            to="/admin/subcategory/update"
            state={{ subCategory: row }}
            className="mx-2 my-2 group"
          >
            <PencilSquare className="w-5 h-5 text-dash4 mr-3" />
          </Link>
          <Link
            className="mx-2 my-2 group"
            onClick={() => handleDeleteConfirmation(row.sub_category_id)}
            style={{ marginLeft: "5px" }}
          >
            <Trash className="w-5 h-5 text-dash3 mr-3" />
          </Link>
        </div>
      ),
    },
    {
      name: "Name",
      selector: (row) => row.sub_category_name.toUpperCase(),
      sortable: true,
      width: "200px",
    },
    {
      name: "Status",
      selector: (row) => row.status ? row.status.toUpperCase() : "NOT AVAILABLE",
      sortable: true,
      cell: (row) => (
        <span className={row.status === "active" ? "text-dash" : "text-dash3"}>
          {row.status ? row.status.toUpperCase() : "NOT AVAILABLE"}
        </span>
      ),
      width: "150px",
    },
    {
      name: "Category",
      selector: (row) => row.category_name,
      sortable: true,
      width: "200px",
    },
  ];

  const filteredItems = subCategory.filter((item) =>
    Object.values(item).some(
      (v) => typeof v === "string" && v.toLowerCase().includes(filterText.toLowerCase())
    )
  );

  const data = filteredItems.map((sc) => ({
    sub_category_name: sc.sub_category_name || "NOT AVAILABLE",
    status: sc.status || "NOT AVAILABLE",
    category_name: sc.category_name ? sc.category_name.toUpperCase() : "NOT AVAILABLE",
    sub_category_id: sc.sub_category_id,
    category_id: sc.category_id,
  }));

  const handleCreateSubcategory = () => {
    navigate("/admin/subcategory/create", { state: { categoryId, categoryName } });
  };

  return (
    <div>
      <MainLayouts>
        <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
          <section className="w-full pr-[3%] pt-3 my-3" style={{ backgroundColor: "#F7F7F7" }}>
            <nav className="bg-gray-200 py-0 px-4 rounded-lg">
              <ol className="list-none p-0 inline-flex mb-2">
                <li className="flex items-center">
                  <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">Category</p>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">SubCategory</p>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <p className="text-orange1">List</p>
                </li>
              </ol>
            </nav>
          </section>
          <div className="pb-4 bg-white dark:bg-gray-900 flex flex-wrap justify-between">
            <div className="relative w-full sm:w-1/2 mb-4 sm:mb-0">
              <input
                type="search"
                className="block w-full px-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                placeholder="Search"
                value={filterText}
                onChange={handleFilter}
              />
            </div>
            <button
              onClick={handleCreateSubcategory}
              className="m-1 w-full sm:w-auto text-dGrey3 bg-orange1 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center"
            >
              Create Subcategory
            </button>
            <Link
              to="/admin/category/list"
              state={{ id: categoryId }}
              className="m-1 w-full sm:w-auto text-dGrey3 bg-darkGreen3 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center"
            >
              <span className="flex justify-center sm:justify-start mx-2">
                <ArrowLeftSquare className="mr-2" />
                Back To Category
              </span>
            </Link>
          </div>
          {loading ? (
            <Spinner loading={loading} />
          ) : (
            <DataTable
              columns={columns}
              data={data}
              pagination
              striped
              highlightOnHover
              noDataComponent={<div>No subCategory found</div>}
              customStyles={CustomStyles}
            />
          )}
        </div>
      </MainLayouts>
    </div>
  );
};

export default SubCategoryList;
